import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, first } from 'rxjs';
import { indicate } from 'src/app/operators';
import {
  ConsolidationMappingViewModel,
  ConsolidationMappingFieldType,
  EsraPipingAPIClient,
  PipingObjectType,
} from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { getMappingFieldTypeOptions, PIPING_OBJECT_TYPE_OPTIONS } from '../../consolidation-mapping-dictionary.model';

/** Dialog input data for `ConsolidationMappingAddEditComponent`. */
export interface ConsolidationMappingAddEditDialogData {
  /** The mapping being edited, or `null` when creating a new mapping. */
  mapping: ConsolidationMappingViewModel | null;
  /** Pre-selected Component filter carried over from the parent grid, for create mode only. */
  defaultPipingObjectType?: PipingObjectType;
  /** Pre-selected Field Type filter carried over from the parent grid, for create mode only. */
  defaultMappingFieldType?: ConsolidationMappingFieldType;
}

/** Backend duplicate-mapping error payload shape, per `errorCode: "DUPLICATE_MAPPING"`. */
interface DuplicateMappingErrorResponse {
  errorCode?: string;
  message?: string;
}

/**
 * Add/Edit dialog for a single Consolidation Mapping record.
 *
 * Why this exists: replicates the ESRA 1 "Consolidation Mapping" admin flow where a
 * Document Value (`originalValue`) is mapped to a single Consolidated Value (`mappedValue`)
 * for a given Component (`pipingObjectType`) and Dictionary (`targetSearchIndex`).
 *
 * Validation strategy: only required-field validation is enforced client-side. Uniqueness
 * across all 4 fields is validated exclusively by the backend -- the frontend must not
 * re-implement the legacy "one mapping per consolidated value" rule. When the backend
 * rejects a duplicate (400 with `errorCode: "DUPLICATE_MAPPING"`), its message is shown
 * verbatim to the user.
 */
@Component({
    selector: 'app-consolidation-mapping-add-edit',
    templateUrl: './consolidation-mapping-add-edit.component.html',
    styleUrls: ['./consolidation-mapping-add-edit.component.scss'],
    standalone: false
})
export class ConsolidationMappingAddEditComponent implements OnInit {
  readonly pipingObjectTypeOptions = PIPING_OBJECT_TYPE_OPTIONS;

  inputFormGroup: UntypedFormGroup;
  title: string = '';
  genericMissingValueMessage: string = 'This is a required field';
  submitClicked: boolean = false;
  isEditMode: boolean = false;

  /** True while the API save call is in-flight -- prevents double-submission. */
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Inline API-level error message, including verbatim duplicate-mapping text from the backend. */
  apiErrorMessage: string | null = null;

  constructor(
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly insightsService: InsightsService,
    private readonly formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<ConsolidationMappingAddEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConsolidationMappingAddEditDialogData,
  ) {}

  ngOnInit(): void {
    this.isEditMode = !!this.data.mapping;
    this.title = this.isEditMode ? 'Edit Mapping' : 'Add Mapping';

    this.inputFormGroup = this.formBuilder.group({
      pipingObjectType: [this.data.mapping?.pipingObjectType ?? this.data.defaultPipingObjectType ?? null, Validators.required],
      mappingFieldType: [this.data.mapping?.mappingFieldType ?? this.data.defaultMappingFieldType ?? null, Validators.required],
      mappedValue: [this.data.mapping?.mappedValue ?? '', Validators.required],
      originalValue: [this.data.mapping?.originalValue ?? '', Validators.required],
    });
  }

  get mappingFieldTypeOptions(): { value: ConsolidationMappingFieldType; label: string }[] {
    return getMappingFieldTypeOptions(this.inputFormGroup?.get('pipingObjectType')?.value);
  }

  onPipingObjectTypeChange(): void {
    const mappingFieldTypeControl = this.inputFormGroup.get('mappingFieldType');
    const fieldTypeIsValid = this.mappingFieldTypeOptions.some(option => option.value === mappingFieldTypeControl.value);

    if (!fieldTypeIsValid) {
      mappingFieldTypeControl.setValue(null);
    }
  }

  save(): void {
    this.submitClicked = true;
    this.apiErrorMessage = null;

    if (this.inputFormGroup.invalid) {
      this.inputFormGroup.markAllAsTouched();
      return;
    }

    const payload: ConsolidationMappingViewModel = new ConsolidationMappingViewModel({
      consolidationMappingRecordId: this.data.mapping?.consolidationMappingRecordId,
      pipingObjectType: this.inputFormGroup.get('pipingObjectType').value,
      mappingFieldType: this.inputFormGroup.get('mappingFieldType').value,
      mappedValue: this.inputFormGroup.get('mappedValue').value,
      originalValue: this.inputFormGroup.get('originalValue').value,
    });

    const save$ = this.isEditMode
      ? this.esraApiClient.updateConsolidationMapping(this.data.mapping.consolidationMappingRecordId, payload)
      : this.esraApiClient.createConsolidationMapping(payload);

    save$.pipe(indicate(this.saving$), first()).subscribe({
      next: () => {
        this.snackBarService.showInfo(
          'Mapping saved successfully. Search index refresh has been triggered. Changes may take a few minutes to appear in search results.'
        );
        this.dialogRef.close(true);
      },
      error: (err: unknown) => { this.handleSaveError(err); },
    });
  }

  /**
   * Parses the API error response, distinguishing the JSON duplicate-mapping error
   * (`errorCode: "DUPLICATE_MAPPING"`) from a generic plain-text validation error.
   * The duplicate message is displayed to the user exactly as returned by the backend.
   * @param err The error emitted by the NSwag-generated API client.
   */
  private handleSaveError(err: unknown): void {
    const responseText: string = (err as { response?: string })?.response ?? '';

    // The backend returns a JSON body with `errorCode` for duplicates, but plain text
    // for other 400 validation errors -- try JSON first before assuming plain text.
    let duplicateError: DuplicateMappingErrorResponse | null = null;
    try {
      const parsed: unknown = JSON.parse(responseText);
      if (parsed && typeof parsed === 'object' && (parsed as DuplicateMappingErrorResponse).errorCode === 'DUPLICATE_MAPPING') {
        duplicateError = parsed as DuplicateMappingErrorResponse;
      }
    } catch {
      // Not JSON -- fall through to plain-text handling below.
    }

    if (duplicateError) {
      this.apiErrorMessage = duplicateError.message ?? responseText;
    } else {
      const message: string = responseText || (err as { message?: string })?.message || 'An unexpected error occurred. Please try again.';
      this.apiErrorMessage = message;
    }

    this.insightsService.logException(err as Error);
  }
}
